import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";
import { 
  Home, 
  Building2, 
  Clock, 
  Users, 
  CheckCircle, 
  ChevronDown, 
  ChevronUp,
  Send,
  Phone,
  Mail,
  MapPin,
  Sparkles,
  Shield,
  Zap,
  MessageSquare
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";

const SERVICE_OPTIONS = [
  { value: "cleaning", label: "Cleaning" },
  { value: "painting", label: "Painting" },
  { value: "landscaping", label: "Landscaping" },
  { value: "hvac", label: "HVAC" },
  { value: "plumbing", label: "Plumbing" },
  { value: "electrical", label: "Electrical" },
  { value: "other", label: "Other" }
];

const URGENCY_OPTIONS = [
  { value: "immediate", label: "Immediate" },
  { value: "1-3_days", label: "1–3 days" },
  { value: "4-7_days", label: "4–7 days" },
  { value: "7+_days", label: "7+ days" }
];

const FAQ_ITEMS = [
  {
    question: "What is your response time?",
    answer: "We respond to all property management requests within 48 hours."
  },
  {
    question: "How does pricing work?",
    answer: "Pricing is provided by trusted local professionals based on the scope of work. We focus on clear, upfront estimates so work can be approved confidently before it begins."
  },
  {
    question: "What areas do you serve?",
    answer: "We currently serve Central Texas, including Travis County, Williamson County, Hays County, and Bastrop County. Additional counties opening soon."
  }
];

export default function PropertyManagers() {
  const [formData, setFormData] = useState({
    units: "",
    addresses: "",
    services: [],
    otherDescription: "",
    urgency: "",
    details: "",
    contactName: "",
    email: "",
    phone: ""
  });
  
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const handleServiceToggle = (serviceValue) => {
    setFormData(prev => {
      const services = prev.services.includes(serviceValue)
        ? prev.services.filter(s => s !== serviceValue)
        : [...prev.services, serviceValue];
      return { ...prev, services };
    });
    if (errors.services) {
      setErrors(prev => ({ ...prev, services: null }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    // Units validation
    if (!formData.units) {
      newErrors.units = "Number of units is required";
    } else if (!/^\d+$/.test(formData.units) || parseInt(formData.units) <= 0) {
      newErrors.units = "Please enter a valid number greater than zero";
    }
    
    // Addresses validation
    if (!formData.addresses.trim()) {
      newErrors.addresses = "Property address(es) is required";
    }
    
    // Services validation
    if (formData.services.length === 0) {
      newErrors.services = "Please select at least one service";
    }
    
    // Other description validation (only if "other" is selected)
    if (formData.services.includes("other") && !formData.otherDescription.trim()) {
      newErrors.otherDescription = "Please describe the other service needed";
    }
    
    // Urgency validation
    if (!formData.urgency) {
      newErrors.urgency = "Please select an urgency level";
    }
    
    // Contact name validation
    if (!formData.contactName.trim()) {
      newErrors.contactName = "Contact name is required";
    }
    
    // Email validation
    if (!formData.email.trim()) {
      newErrors.email = "Email address is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Please enter a valid email address";
    }
    
    // Phone validation
    if (!formData.phone.trim()) {
      newErrors.phone = "Phone number is required";
    } else if (!/^[\d\s\-\(\)\+]{10,}$/.test(formData.phone.replace(/\s/g, ''))) {
      newErrors.phone = "Please enter a valid phone number";
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      toast.error("Please fix the errors in the form");
      return;
    }
    
    setSubmitting(true);
    
    try {
      // Submit to backend
      await axios.post(`${API}/property-manager-requests`, {
        ...formData,
        source: "Property Managers Page"
      });
      
      setSubmitted(true);
      toast.success("Request submitted successfully!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to submit request. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="border-b border-gray-100 sticky top-0 bg-white z-50">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-xl bg-[#0FA3B1] flex items-center justify-center">
              <Home className="w-5 h-5 text-white" />
            </div>
            <span className="font-manrope font-bold text-xl text-[#1F2933]">Contractor Hub LLC</span>
          </Link>
          
          <nav className="hidden md:flex items-center gap-6">
            <Link to="/" className="text-[#1F2933]/70 hover:text-[#1F2933] transition-colors">Home</Link>
            <Link to="/#why-us" className="text-[#1F2933]/70 hover:text-[#1F2933] transition-colors">Why Us</Link>
            <Link to="/property-managers" className="text-[#0FA3B1] font-medium">Property Managers</Link>
            <Link to="/login">
              <Button variant="outline" className="rounded-xl">Sign In</Button>
            </Link>
          </nav>
        </div>
      </header>

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-[#0FA3B1]/5 to-white py-16 sm:py-24">
        <div className="max-w-6xl mx-auto px-4 text-center">
          <div className="inline-flex items-center gap-2 bg-[#0FA3B1]/10 text-[#0FA3B1] px-4 py-2 rounded-full text-sm font-medium mb-6">
            <Building2 className="w-4 h-4" />
            For Property Managers & HOAs
          </div>
          
          <h1 className="font-manrope font-bold text-3xl sm:text-4xl lg:text-5xl text-[#1F2933] mb-4">
            Get Trusted Home Services<br />for Your Properties
          </h1>
          
          <p className="text-lg text-[#1F2933]/70 max-w-2xl mx-auto">
            Dedicated service solutions for property managers, HOAs, and portfolios — fast response, reliable results.
          </p>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-12 sm:py-16">
        <div className="max-w-6xl mx-auto px-4">
          <div className="grid lg:grid-cols-5 gap-12">
            {/* Form Column */}
            <div className="lg:col-span-3">
              {submitted ? (
                <div className="bg-green-50 border border-green-200 rounded-2xl p-8 text-center">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <CheckCircle className="w-8 h-8 text-green-600" />
                  </div>
                  <h2 className="font-manrope font-bold text-2xl text-[#1F2933] mb-2">
                    Request Received
                  </h2>
                  <p className="text-[#1F2933]/70 mb-6">
                    Our team will respond within 48 hours.
                  </p>
                  <Button 
                    onClick={() => {
                      setSubmitted(false);
                      setFormData({
                        units: "",
                        addresses: "",
                        services: [],
                        otherDescription: "",
                        urgency: "",
                        details: "",
                        contactName: "",
                        email: "",
                        phone: ""
                      });
                    }}
                    variant="outline"
                  >
                    Submit Another Request
                  </Button>
                </div>
              ) : (
                <div className="bg-white border border-gray-200 rounded-2xl p-6 sm:p-8 shadow-sm">
                  <h2 className="font-manrope font-bold text-2xl text-[#1F2933] mb-6">
                    Submit Your Service Request
                  </h2>
                  
                  <form onSubmit={handleSubmit} className="space-y-6">
                    {/* How many units? */}
                    <div>
                      <Label htmlFor="units" className="text-[#1F2933] font-medium">
                        How many units? <span className="text-red-500">*</span>
                      </Label>
                      <Input
                        id="units"
                        name="units"
                        type="text"
                        value={formData.units}
                        onChange={handleChange}
                        placeholder="e.g. 12, 48, 120"
                        className={`mt-2 ${errors.units ? 'border-red-500' : ''}`}
                        data-testid="units-input"
                      />
                      {errors.units && (
                        <p className="text-red-500 text-sm mt-1">{errors.units}</p>
                      )}
                    </div>

                    {/* Property Address(s) */}
                    <div>
                      <Label htmlFor="addresses" className="text-[#1F2933] font-medium">
                        Property Address(s) <span className="text-red-500">*</span>
                      </Label>
                      <Textarea
                        id="addresses"
                        name="addresses"
                        value={formData.addresses}
                        onChange={handleChange}
                        placeholder="Enter property address(es)&#10;Street, City, State, ZIP&#10;(You may list multiple addresses)"
                        className={`mt-2 min-h-[120px] ${errors.addresses ? 'border-red-500' : ''}`}
                        data-testid="addresses-input"
                      />
                      {errors.addresses && (
                        <p className="text-red-500 text-sm mt-1">{errors.addresses}</p>
                      )}
                    </div>

                    {/* Services Needed */}
                    <div>
                      <Label className="text-[#1F2933] font-medium">
                        Services Needed (Select all that apply) <span className="text-red-500">*</span>
                      </Label>
                      <div className={`mt-3 space-y-3 p-4 border rounded-xl ${errors.services ? 'border-red-500' : 'border-gray-200'}`}>
                        {SERVICE_OPTIONS.map(service => (
                          <div key={service.value} className="flex items-center gap-3">
                            <Checkbox
                              id={`service-${service.value}`}
                              checked={formData.services.includes(service.value)}
                              onCheckedChange={() => handleServiceToggle(service.value)}
                              data-testid={`service-${service.value}`}
                            />
                            <label 
                              htmlFor={`service-${service.value}`}
                              className="text-[#1F2933] cursor-pointer"
                            >
                              {service.label}
                            </label>
                          </div>
                        ))}
                      </div>
                      {errors.services && (
                        <p className="text-red-500 text-sm mt-1">{errors.services}</p>
                      )}
                    </div>

                    {/* Other service description (conditional) */}
                    {formData.services.includes("other") && (
                      <div className="animate-fade-in">
                        <Label htmlFor="otherDescription" className="text-[#1F2933] font-medium">
                          Other service description <span className="text-red-500">*</span>
                        </Label>
                        <Input
                          id="otherDescription"
                          name="otherDescription"
                          type="text"
                          value={formData.otherDescription}
                          onChange={handleChange}
                          placeholder="Describe other service"
                          className={`mt-2 ${errors.otherDescription ? 'border-red-500' : ''}`}
                          data-testid="other-description-input"
                        />
                        {errors.otherDescription && (
                          <p className="text-red-500 text-sm mt-1">{errors.otherDescription}</p>
                        )}
                      </div>
                    )}

                    {/* Urgency Level */}
                    <div>
                      <Label className="text-[#1F2933] font-medium">
                        Urgency Level <span className="text-red-500">*</span>
                      </Label>
                      <RadioGroup
                        value={formData.urgency}
                        onValueChange={(value) => {
                          setFormData(prev => ({ ...prev, urgency: value }));
                          if (errors.urgency) setErrors(prev => ({ ...prev, urgency: null }));
                        }}
                        className={`mt-3 grid grid-cols-2 gap-3 ${errors.urgency ? 'ring-2 ring-red-500 rounded-xl p-1' : ''}`}
                      >
                        {URGENCY_OPTIONS.map(option => (
                          <div key={option.value} className="flex items-center">
                            <RadioGroupItem
                              value={option.value}
                              id={`urgency-${option.value}`}
                              className="peer sr-only"
                            />
                            <label
                              htmlFor={`urgency-${option.value}`}
                              className={`w-full p-3 border rounded-xl cursor-pointer text-center transition-all ${
                                formData.urgency === option.value
                                  ? 'bg-[#0FA3B1]/10 border-[#0FA3B1] text-[#0FA3B1]'
                                  : 'border-gray-200 hover:border-[#0FA3B1]/50'
                              }`}
                              data-testid={`urgency-${option.value}`}
                            >
                              {option.label}
                            </label>
                          </div>
                        ))}
                      </RadioGroup>
                      {errors.urgency && (
                        <p className="text-red-500 text-sm mt-1">{errors.urgency}</p>
                      )}
                    </div>

                    {/* Additional Job Details */}
                    <div>
                      <Label htmlFor="details" className="text-[#1F2933] font-medium">
                        Additional Job Details <span className="text-[#1F2933]/50">(Optional)</span>
                      </Label>
                      <Textarea
                        id="details"
                        name="details"
                        value={formData.details}
                        onChange={handleChange}
                        placeholder="Describe the issue, access instructions, tenant impact, preferred schedule, or any important notes."
                        className="mt-2 min-h-[100px]"
                        data-testid="details-input"
                      />
                    </div>

                    {/* Divider */}
                    <div className="border-t border-gray-200 pt-6">
                      <h3 className="font-semibold text-[#1F2933] mb-4">Contact Information</h3>
                    </div>

                    {/* Contact Name */}
                    <div>
                      <Label htmlFor="contactName" className="text-[#1F2933] font-medium">
                        Contact Name <span className="text-red-500">*</span>
                      </Label>
                      <Input
                        id="contactName"
                        name="contactName"
                        type="text"
                        value={formData.contactName}
                        onChange={handleChange}
                        placeholder="Your name"
                        className={`mt-2 ${errors.contactName ? 'border-red-500' : ''}`}
                        data-testid="contact-name-input"
                      />
                      {errors.contactName && (
                        <p className="text-red-500 text-sm mt-1">{errors.contactName}</p>
                      )}
                    </div>

                    {/* Email Address */}
                    <div>
                      <Label htmlFor="email" className="text-[#1F2933] font-medium">
                        Email Address <span className="text-red-500">*</span>
                      </Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="you@company.com"
                        className={`mt-2 ${errors.email ? 'border-red-500' : ''}`}
                        data-testid="email-input"
                      />
                      {errors.email && (
                        <p className="text-red-500 text-sm mt-1">{errors.email}</p>
                      )}
                    </div>

                    {/* Phone Number */}
                    <div>
                      <Label htmlFor="phone" className="text-[#1F2933] font-medium">
                        Phone Number <span className="text-red-500">*</span>
                      </Label>
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={handleChange}
                        placeholder="(512) 555-0123"
                        className={`mt-2 ${errors.phone ? 'border-red-500' : ''}`}
                        data-testid="phone-input"
                      />
                      {errors.phone && (
                        <p className="text-red-500 text-sm mt-1">{errors.phone}</p>
                      )}
                    </div>

                    {/* Submit Button */}
                    <Button
                      type="submit"
                      className="w-full bg-[#0FA3B1] hover:bg-[#0D8C98] text-white py-6 rounded-xl"
                      disabled={submitting}
                      data-testid="submit-request-btn"
                    >
                      {submitting ? (
                        <div className="flex items-center gap-2">
                          <div className="w-5 h-5 border-2 border-white/20 border-t-white rounded-full animate-spin"></div>
                          Submitting...
                        </div>
                      ) : (
                        <div className="flex items-center gap-2">
                          <Send className="w-5 h-5" />
                          Submit Request
                        </div>
                      )}
                    </Button>
                  </form>
                </div>
              )}
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-2 space-y-8">
              {/* Why Property Managers Choose Us */}
              <div className="bg-[#0FA3B1]/5 rounded-2xl p-6">
                <h3 className="font-manrope font-bold text-lg text-[#1F2933] mb-4 flex items-center gap-2">
                  <Shield className="w-5 h-5 text-[#0FA3B1]" />
                  Why Property Managers Choose Us
                </h3>
                <ul className="space-y-3">
                  {[
                    "Serve tenants faster with a single point of contact",
                    "Trusted local professionals across service categories",
                    "Centralized requests across single or multiple properties",
                    "Flexible scheduling, including urgent requests",
                    "Clear communication from request to completion"
                  ].map((item, index) => (
                    <li key={item} className="flex items-start gap-3 text-[#1F2933]/80">
                      <CheckCircle className="w-5 h-5 text-[#0FA3B1] shrink-0 mt-0.5" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* How It Works */}
              <div className="bg-white border border-gray-200 rounded-2xl p-6">
                <h3 className="font-manrope font-bold text-lg text-[#1F2933] mb-4 flex items-center gap-2">
                  <Zap className="w-5 h-5 text-[#0FA3B1]" />
                  How It Works
                </h3>
                <ol className="space-y-4">
                  {[
                    "Submit your service request",
                    "We review and route to trusted local professionals",
                    "Receive a response within 48 hours",
                    "Work gets scheduled and completed"
                  ].map((step, index) => (
                    <li key={step} className="flex items-start gap-3">
                      <div className="w-6 h-6 rounded-full bg-[#0FA3B1] text-white text-sm font-bold flex items-center justify-center shrink-0">
                        {index + 1}
                      </div>
                      <span className="text-[#1F2933]/80">{step}</span>
                    </li>
                  ))}
                </ol>
              </div>

              {/* What You Get */}
              <div className="bg-white border border-gray-200 rounded-2xl p-6">
                <h3 className="font-manrope font-bold text-lg text-[#1F2933] mb-4 flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-[#0FA3B1]" />
                  What You Get
                </h3>
                <ul className="space-y-3">
                  {[
                    "Job scope captured clearly",
                    "Trusted professionals assigned",
                    "Response within 48 hours",
                    "Flexible scheduling based on urgency",
                    "Single point of contact for follow-up"
                  ].map((item, index) => (
                    <li key={item} className="flex items-center gap-3 text-[#1F2933]/80">
                      <div className="w-2 h-2 rounded-full bg-[#0FA3B1]"></div>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="bg-gray-50 py-12 sm:py-16">
        <div className="max-w-3xl mx-auto px-4">
          <h2 className="font-manrope font-bold text-2xl sm:text-3xl text-[#1F2933] text-center mb-8">
            Frequently Asked Questions
          </h2>
          
          <div className="space-y-4">
            {FAQ_ITEMS.map((faq, index) => (
              <div 
                key={faq.question}
                className="bg-white rounded-xl border border-gray-200 overflow-hidden"
              >
                <button
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                  className="w-full p-4 sm:p-5 flex items-center justify-between text-left"
                  data-testid={`faq-${index}`}
                >
                  <span className="font-medium text-[#1F2933] pr-4">{faq.question}</span>
                  {openFaq === index ? (
                    <ChevronUp className="w-5 h-5 text-[#0FA3B1] shrink-0" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-[#1F2933]/50 shrink-0" />
                  )}
                </button>
                {openFaq === index && (
                  <div className="px-4 sm:px-5 pb-4 sm:pb-5 text-[#1F2933]/70 animate-fade-in">
                    {faq.answer}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#1F2933] text-white py-12">
        <div className="max-w-6xl mx-auto px-4">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {/* Brand */}
            <div>
              <div className="flex items-center gap-2 mb-4">
                <div className="w-10 h-10 rounded-xl bg-[#0FA3B1] flex items-center justify-center">
                  <Home className="w-5 h-5 text-white" />
                </div>
                <span className="font-manrope font-bold text-xl">Contractor Hub LLC</span>
              </div>
              <p className="text-white/60 text-sm">
                Trusted home services for property managers, HOAs, and portfolios in Central Texas.
              </p>
            </div>

            {/* Contact */}
            <div>
              <h4 className="font-semibold mb-4">Contact</h4>
              <ul className="space-y-3 text-white/60 text-sm">
                <li className="flex items-center gap-2">
                  <Mail className="w-4 h-4" />
                  support@contractorhub.com
                </li>
                <li className="flex items-center gap-2">
                  <Phone className="w-4 h-4" />
                  (512) 555-0100
                </li>
              </ul>
            </div>

            {/* Quick Links */}
            <div>
              <h4 className="font-semibold mb-4">Quick Links</h4>
              <ul className="space-y-2 text-white/60 text-sm">
                <li>
                  <Link to="/" className="hover:text-white transition-colors">Home</Link>
                </li>
                <li>
                  <Link to="/property-managers" className="hover:text-white transition-colors">Property Managers</Link>
                </li>
                <li>
                  <Link to="/login" className="hover:text-white transition-colors">Sign In</Link>
                </li>
              </ul>
            </div>

            {/* Legal */}
            <div>
              <h4 className="font-semibold mb-4">Legal</h4>
              <ul className="space-y-2 text-white/60 text-sm">
                <li>
                  <a href="#" className="hover:text-white transition-colors">Privacy Policy</a>
                </li>
                <li>
                  <a href="#" className="hover:text-white transition-colors">Terms of Service</a>
                </li>
              </ul>
            </div>
          </div>

          <div className="border-t border-white/10 mt-8 pt-8 text-center text-white/40 text-sm">
            © {new Date().getFullYear()} Contractor Hub LLC. All rights reserved.
          </div>
        </div>
      </footer>
    </div>
  );
}
